/* Ce que porte une fiche, champ par champ.
 *
 * Décrit en données plutôt qu'en JSX : la fiche se lit et se modifie avec le
 * même descripteur, et ajouter un champ est une ligne, pas trois écrans.
 *
 * Le niveau de détail est celui d'un ERP — identité, administratif, logistique,
 * commercial — parce que c'est ce qu'on a sous la main ailleurs et qu'une fiche
 * incomplète renvoie à l'ailleurs.
 */

import type { GroupeDef } from "@/ui/Fiche";
import type { Client, Produit, Profil, Usine } from "@/types/metier";

/* Les incoterms qu'on pratique à l'export agroalimentaire. Pas les onze :
   ceux que nos usines et nos clients emploient réellement. */
export const INCOTERMS = [
  { v: "EXW", n: "EXW — à l'usine" },
  { v: "FCA", n: "FCA — remis au transporteur" },
  { v: "FOB", n: "FOB — à bord" },
  { v: "CFR", n: "CFR — coût et fret" },
  { v: "CIF", n: "CIF — coût, assurance, fret" },
  { v: "CPT", n: "CPT — port payé" },
  { v: "CIP", n: "CIP — port payé, assuré" },
  { v: "DAP", n: "DAP — rendu au lieu convenu" },
  { v: "DDP", n: "DDP — rendu droits acquittés" },
] as const;

export const LANGUES = [
  { v: "fr", n: "Français" },
  { v: "en", n: "Anglais" },
  { v: "es", n: "Espagnol" },
  { v: "it", n: "Italien" },
  { v: "de", n: "Allemand" },
  { v: "ar", n: "Arabe" },
] as const;

/* Ce qu'on dirait du payeur. Trois mots suffisent, et ils se disent en réunion. */
export const CONFIANCES = [
  { v: "bonne", n: "Bonne — paie à l'heure" },
  { v: "normale", n: "Normale — rien à signaler" },
  { v: "surveiller", n: "À surveiller — retards" },
  { v: "douteuse", n: "Douteuse — prépaiement" },
] as const;

export const PAIEMENTS = [
  { v: "comptant", n: "Comptant" },
  { v: "prepaiement", n: "Prépaiement" },
  { v: "30j", n: "30 jours" },
  { v: "30j_fdm", n: "30 jours fin de mois" },
  { v: "45j", n: "45 jours" },
  { v: "60j", n: "60 jours" },
  { v: "60j_fdm", n: "60 jours fin de mois" },
  { v: "90j", n: "90 jours" },
  { v: "credoc", n: "Crédit documentaire" },
] as const;

export const SEGMENTS = [
  { v: "grande_distribution", n: "Grande distribution" },
  { v: "grossiste", n: "Grossiste" },
  { v: "importateur", n: "Importateur" },
  { v: "industrie", n: "Industrie" },
  { v: "horeca", n: "CHR / restauration" },
  { v: "detail", n: "Détail / épicerie" },
  { v: "e_commerce", n: "E-commerce" },
] as const;

export const UNITES = [
  { v: "kg", n: "Kilogramme" },
  { v: "t", n: "Tonne" },
  { v: "l", n: "Litre" },
  { v: "piece", n: "Pièce" },
  { v: "carton", n: "Carton" },
  { v: "palette", n: "Palette" },
] as const;

export const NIVEAUX = [
  { v: "agent", n: "Agent — tout le commerce, usines comprises" },
  { v: "admin", n: "Direction — en plus : prix d'achat, barème, facturation" },
] as const;

/** Le libellé d'une valeur, pour l'afficher ailleurs que dans la fiche. */
export function libelle(
  liste: readonly { v: string; n: string }[],
  v: string | null | undefined,
): string {
  if (!v) return "—";
  return liste.find((o) => o.v === v)?.n ?? v;
}

/** Le libellé court, sans l'explication qui suit le tiret. */
export function libelleCourt(
  liste: readonly { v: string; n: string }[],
  v: string | null | undefined,
): string {
  const l = libelle(liste, v);
  const i = l.indexOf(" — ");
  return i > 0 ? l.slice(0, i) : l;
}

/* ──────────────────────────────── le client ─────────────────────────────── */

export const GROUPES_CLIENT: readonly GroupeDef<Client>[] = [
  {
    titre: "Identité",
    champs: [
      { cle: "nom", nom: "Raison sociale" },
      { cle: "type", nom: "Nature", type: "select",
        options: [{ v: "client", n: "Client" }, { v: "lead", n: "Prospect" }] },
      { cle: "segment", nom: "Segment", type: "select", options: SEGMENTS },
      { cle: "categorie", nom: "Catégorie", aide: "Classement interne, libre." },
      { cle: "depuis", nom: "Client depuis", type: "date" },
      { cle: "langue", nom: "Langue des documents", type: "select", options: LANGUES },
    ],
  },
  {
    titre: "Coordonnées",
    champs: [
      { cle: "adresse", nom: "Adresse", large: true },
      { cle: "code_postal", nom: "Code postal", type: "mono" },
      { cle: "ville", nom: "Ville" },
      { cle: "pays", nom: "Pays" },
      { cle: "telephone", nom: "Téléphone", type: "tel" },
      { cle: "email", nom: "Courriel", type: "mail" },
      { cle: "site", nom: "Site", type: "url" },
    ],
  },
  {
    titre: "Administratif",
    note: "ce qui figure sur les documents",
    champs: [
      { cle: "tva", nom: "N° de TVA", type: "mono" },
      { cle: "registre", nom: "Registre du commerce", type: "mono" },
      { cle: "incoterm", nom: "Incoterm habituel", type: "select", options: INCOTERMS },
      { cle: "conditions_paiement", nom: "Conditions de paiement", type: "select",
        options: PAIEMENTS },
    ],
  },
  {
    titre: "Crédit",
    note: "ce qu'on lui accorde",
    champs: [
      { cle: "limite_encours", nom: "Limite d'encours", type: "euro" },
      { cle: "assurance_credit", nom: "Encours assuré", type: "euro",
        aide: "Au-delà, on vend à découvert." },
      { cle: "confiance", nom: "Confiance", type: "select", options: CONFIANCES },
    ],
  },
  {
    titre: "Notes",
    champs: [{ cle: "notes", nom: "Notes", type: "zone" }],
  },
];

/* ──────────────────────────────── l'usine ───────────────────────────────── */

export const GROUPES_USINE: readonly GroupeDef<Usine>[] = [
  {
    titre: "Identité",
    champs: [
      { cle: "code", nom: "Code", type: "mono", fige: true },
      { cle: "nom", nom: "Raison sociale" },
      { cle: "categorie", nom: "Catégorie" },
      { cle: "actif", nom: "Active", type: "bool" },
      { cle: "langue", nom: "Langue d'échange", type: "select", options: LANGUES },
    ],
  },
  {
    titre: "Coordonnées",
    champs: [
      { cle: "adresse", nom: "Adresse", large: true },
      { cle: "code_postal", nom: "Code postal", type: "mono" },
      { cle: "ville", nom: "Ville" },
      { cle: "pays", nom: "Pays" },
      { cle: "telephone", nom: "Téléphone", type: "tel" },
      { cle: "email", nom: "Courriel", type: "mail" },
      { cle: "site", nom: "Site", type: "url" },
    ],
  },
  {
    titre: "Administratif",
    champs: [
      { cle: "tva", nom: "N° de TVA", type: "mono" },
      { cle: "registre", nom: "Registre du commerce", type: "mono" },
      { cle: "prefixe_facture", nom: "Préfixe de facture", type: "mono",
        aide: "Ce qui précède le numéro sur ses factures." },
    ],
  },
  {
    titre: "Commerce",
    champs: [
      { cle: "incoterm", nom: "Incoterm habituel", type: "select", options: INCOTERMS },
      { cle: "conditions_paiement", nom: "Conditions de paiement", type: "select",
        options: PAIEMENTS },
      { cle: "delai_production", nom: "Délai de production",
        aide: "Tel qu'elle le dit : « 3 à 4 semaines »." },
    ],
  },
  {
    titre: "Notes",
    champs: [{ cle: "notes", nom: "Notes", type: "zone" }],
  },
];

/* ─────────────────────────────── le produit ─────────────────────────────── */

export const GROUPES_PRODUIT: readonly GroupeDef<Produit>[] = [
  {
    titre: "Identité",
    champs: [
      { cle: "ref", nom: "Référence", type: "mono" },
      { cle: "nom", nom: "Désignation", large: true },
      { cle: "gamme", nom: "Gamme" },
      { cle: "origine", nom: "Origine" },
      { cle: "actif", nom: "Au catalogue", type: "bool" },
      { cle: "ean", nom: "Code-barres (EAN)", type: "mono" },
    ],
  },
  {
    titre: "Conditionnement",
    note: "ce qu'un acheteur demande avant de commander",
    champs: [
      { cle: "conditionnement", nom: "Conditionnement", large: true,
        aide: "Tel qu'il figure sur l'offre : « 12 × 1 kg »." },
      { cle: "unite", nom: "Unité de vente", type: "select", options: UNITES },
      { cle: "poids_net", nom: "Poids net", type: "nombre", unite: "kg" },
      { cle: "poids_brut", nom: "Poids brut", type: "nombre", unite: "kg" },
      { cle: "par_carton", nom: "Unités par carton", type: "nombre" },
      { cle: "par_palette", nom: "Cartons par palette", type: "nombre" },
    ],
  },
  {
    titre: "Qualité et douane",
    champs: [
      { cle: "code_douane", nom: "Code douanier", type: "mono",
        aide: "Nomenclature à l'export." },
      { cle: "dluo", nom: "Durée de vie", aide: "« 24 mois », telle qu'elle est donnée." },
      { cle: "allergenes", nom: "Allergènes", large: true },
      { cle: "ingredients", nom: "Ingrédients", type: "zone" },
    ],
  },
  {
    titre: "Notes",
    champs: [{ cle: "notes", nom: "Notes", type: "zone" }],
  },
];

/* ──────────────────────────────── l'agent ───────────────────────────────── */

export const GROUPES_AGENT: readonly GroupeDef<Profil>[] = [
  {
    titre: "Identité",
    champs: [
      { cle: "nom", nom: "Nom" },
      { cle: "telephone", nom: "Téléphone", type: "tel" },
      { cle: "email", nom: "Courriel", type: "mail",
        aide: "Sert à rattacher la fiche au compte : la changer après la première connexion ne change pas l'adresse de connexion." },
      { cle: "niveau", nom: "Niveau d'accès", type: "select", options: NIVEAUX },
      { cle: "actif", nom: "Actif", type: "bool",
        aide: "Un agent inactif ne peut plus se connecter." },
    ],
  },
];

/* Ce que chacun corrige sur sa propre fiche. Le niveau d'accès n'y est pas, et
   ce n'est pas l'écran qui le tient : la fonction appelée ne nomme que ces
   deux colonnes. */
export const GROUPES_MES_COORDONNEES: readonly GroupeDef<Profil>[] = [
  {
    titre: "Mes coordonnées",
    note: "ce que vous pouvez corriger vous-même",
    champs: [
      { cle: "nom", nom: "Nom" },
      { cle: "telephone", nom: "Téléphone", type: "tel" },
      { cle: "email", nom: "Courriel", type: "mail", fige: true,
        aide: "C'est votre adresse de connexion : la direction seule peut la changer." },
      { cle: "niveau", nom: "Niveau d'accès", type: "select", options: NIVEAUX, fige: true },
    ],
  },
];
